import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, ChartBar, Globe } from "@phosphor-icons/react";
import KoshMark from "../../components/KoshMark";

export default function AnalyticsPanel({ token }) {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);
  const [kosh, setKosh] = useState(null);
  const [evalset, setEvalset] = useState(null);
  const [corrections, setCorrections] = useState(null);

  const load = async (m = months) => {
    setLoading(true);
    try {
      const [aRes, kRes, eRes, cRes] = await Promise.allSettled([
        axios.get(`${API}/admin/analytics?months=${m}`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/analytics/kosh?days=${m * 30}`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/analytics/kosh-evalset?limit=50`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/analytics/kosh-corrections?limit=20`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (aRes.status === "fulfilled") setData(aRes.value.data);
      if (kRes.status === "fulfilled") setKosh(kRes.value.data);
      if (eRes.status === "fulfilled") setEvalset(eRes.value.data);
      if (cRes.status === "fulfilled") setCorrections(cRes.value.data);
      if (aRes.status === "rejected") toast.error("Could not load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(months);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  if (loading && !data) return <div className="text-[#8E8E93] text-sm px-4 py-6" data-testid="analytics-loading">Crunching visitor data…</div>;

  const monthly = data?.monthly || [];
  const maxTotal = Math.max(1, ...monthly.map((m) => m.total));
  const totalWindow = data?.total_in_window || 0;
  const uniqueWindow = monthly.reduce((acc, m) => acc + m.unique, 0);
  const current = monthly[monthly.length - 1] || { total: 0, unique: 0, month: "—" };
  const prev = monthly[monthly.length - 2];
  const totalDelta = prev ? Math.round(((current.total - prev.total) / Math.max(1, prev.total)) * 100) : null;
  const uniqueDelta = prev ? Math.round(((current.unique - prev.unique) / Math.max(1, prev.unique)) * 100) : null;

  const fmtMonth = (yyyymm) => {
    if (!yyyymm) return "—";
    const [y, m] = yyyymm.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-US", { month: "short", year: "2-digit" });
  };

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="text-overline">◢ Visitor analytics · last {months} month{months > 1 ? "s" : ""}</div>
        <div className="flex items-center gap-2">
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            data-testid="analytics-range"
            className="ww-input bg-[#050505] py-2 px-3 text-xs"
            style={{ height: "auto" }}
          >
            <option value={1}>1 month</option>
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </select>
          <button onClick={() => load()} className="btn-secondary" data-testid="analytics-refresh"><ArrowsClockwise size={14} /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-8">
        <StatTile label={`This month (${fmtMonth(current.month)})`} value={current.total} delta={totalDelta} accent="#FF4500" testid="stat-month-total" />
        <StatTile label="Unique this month" value={current.unique} delta={uniqueDelta} accent="#DFFF00" testid="stat-month-unique" />
        <StatTile label={`Total · ${months}mo`} value={totalWindow} accent="#00E5FF" testid="stat-window-total" />
        <StatTile label={`Unique sum · ${months}mo`} value={uniqueWindow} note="monthly buckets, not de-duped across months" accent="#FF6B35" testid="stat-window-unique" />
      </div>

      {/* KOSH usage — turns the copilot into a demand-signal instrument */}
      {kosh && (
        <div className="ww-card p-6 mb-8" data-testid="analytics-kosh-card">
          <div className="flex items-center justify-between mb-4">
            <div className="text-overline flex items-center gap-2">
              <KoshMark size={14} tone="on-glass" /> KOSH usage · last {kosh.window_days} days
            </div>
            <div className="font-mono text-[10px] text-[#555]">the copilot as a demand signal</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-6">
            <StatTile label="Conversations" value={kosh.sessions} accent="#FF4500" testid="kosh-stat-sessions" />
            <StatTile label="Engaged (≥3 turns)" value={kosh.engaged_sessions} accent="#DFFF00" note={kosh.sessions > 0 ? `${Math.round((kosh.engaged_sessions / kosh.sessions) * 100)}% of visitors` : "—"} testid="kosh-stat-engaged" />
            <StatTile label="Discovery calls deflected" value={kosh.deflected_calls ?? 0} accent="#00FFB2" note="self-serve wins" testid="kosh-stat-deflected" />
            <StatTile label="Avg reply length" value={`${kosh.avg_reply_chars}`} note="characters" accent="#FF6B35" testid="kosh-stat-reply-len" />
          </div>
          <div className="text-overline mb-3">◢ Top questions</div>
          {kosh.top_questions.length === 0 ? (
            <div className="text-[#555] text-sm">No conversations in this window yet — the copilot is waiting.</div>
          ) : (
            <ul className="space-y-2" data-testid="kosh-top-questions">
              {kosh.top_questions.map((q, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="font-mono text-[10px] text-[#8E8E93] mt-0.5 w-6 shrink-0">#{i + 1}</span>
                  <span className="text-white flex-1">{q.question}</span>
                  <span className="font-mono text-xs text-[#DFFF00] shrink-0">×{q.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* KOSH eval-set — low-rated answers surfaced for prompt-engineering */}
      {evalset && (
        <div className="ww-card p-6 mb-8" data-testid="analytics-kosh-evalset">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="text-overline">◢ KOSH quality · eval-set</div>
            <div className="flex items-center gap-3 font-mono text-[10px]">
              <span className="text-[#00FFB2]">👍 {evalset.counts?.up ?? 0}</span>
              <span className="text-[#FF3060]">👎 {evalset.counts?.down ?? 0}</span>
              <span className="text-[#8E8E93]">
                CSAT: <span className="text-[#DFFF00]">{evalset.counts?.csat_pct ?? "—"}{evalset.counts?.csat_pct != null ? "%" : ""}</span>
              </span>
              <span className="text-[#555]">of {evalset.counts?.total_rated ?? 0} rated</span>
            </div>
          </div>
          {(!evalset.evalset || evalset.evalset.length === 0) ? (
            <div className="text-[#555] text-sm">
              No 👎 answers yet — either KOSH is perfect, or visitors haven't rated anything. As
              ratings come in, poorly-answered questions will surface here for prompt tuning.
            </div>
          ) : (
            <ul className="divide-y divide-[#2A2A2A]" data-testid="kosh-evalset-list">
              {evalset.evalset.map((row) => (
                <li key={row.message_id} className="py-4">
                  <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93] flex items-center gap-2">
                    <span className="text-[#FF3060]">👎</span>
                    <span>Q · {row.rated_at ? new Date(row.rated_at).toLocaleString() : ""}</span>
                    <span className="ml-auto text-[#555]">session {(row.session_id || "").slice(0, 8)}</span>
                  </div>
                  <div className="mt-1 text-[#F5F5F5] text-sm">
                    {row.question || <span className="italic text-[#555]">— no prior user turn found —</span>}
                  </div>
                  {row.note && (
                    <div className="mt-1 pl-2 border-l-2 border-[#DFFF00] text-[12px] text-[#DFFF00] italic">
                      visitor said we should have: "{row.note}"
                    </div>
                  )}
                  <details className="mt-2">
                    <summary className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93] cursor-pointer hover:text-white">
                      ↳ KOSH's answer
                    </summary>
                    <div className="mt-2 pl-3 border-l-2 border-[#FF3060] text-[#D7D7D9] text-sm whitespace-pre-wrap">
                      {row.answer}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* KOSH auto-improve corrections — how visitor feedback is silently shaping KOSH */}
      {corrections && (
        <div className="ww-card p-6 mb-8" data-testid="analytics-kosh-corrections">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="text-overline">◢ KOSH auto-improve · corrections in production</div>
            <div className="font-mono text-[10px] text-[#555]">visitor feedback → live behaviour</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A] mb-5">
            <StatTile label="Correction notes" value={corrections.totals?.correction_notes ?? 0} accent="#DFFF00" note={corrections.totals?.correction_clusters != null ? `${corrections.totals.correction_clusters} unique cluster${corrections.totals.correction_clusters === 1 ? "" : "s"}` : "—"} testid="corr-stat-notes" />
            <StatTile label="Replies improved" value={corrections.totals?.with_corrections ?? 0} note={corrections.totals?.assistant_messages ? `${Math.round(((corrections.totals.with_corrections || 0) / corrections.totals.assistant_messages) * 100)}% of all replies` : "—"} accent="#FF4500" testid="corr-stat-applied" />
            <StatTile label="CSAT · corrected" value={corrections.totals?.corrected_csat_pct != null ? `${corrections.totals.corrected_csat_pct}%` : "—"} accent="#00FFB2" testid="corr-stat-csat-corrected" />
            <StatTile
              label="Δ vs baseline"
              value={corrections.totals?.csat_delta_pct != null ? `${corrections.totals.csat_delta_pct > 0 ? "+" : ""}${corrections.totals.csat_delta_pct}pp` : "—"}
              accent={corrections.totals?.csat_delta_pct != null && corrections.totals.csat_delta_pct >= 0 ? "#00FFB2" : "#FF3060"}
              note={corrections.totals?.baseline_csat_pct != null ? `baseline ${corrections.totals.baseline_csat_pct}%` : "—"}
              testid="corr-stat-delta"
            />
          </div>
          {(!corrections.notes || corrections.notes.length === 0) ? (
            <div className="text-[#555] text-sm">
              No visitor correction notes yet. When someone clicks 👎 on a KOSH reply and types "what should KOSH have said", it lands here — and every future similar question silently uses that correction as a few-shot.
            </div>
          ) : (
            <ul className="divide-y divide-[#2A2A2A]" data-testid="kosh-corrections-list">
              {corrections.notes.map((row) => (
                <li key={row.source_message_id} className="py-3" data-testid={`corr-row-${row.source_message_id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93]">Q</span>
                    <span className="text-white text-sm">{row.question || "—"}</span>
                    {row.cluster_size > 1 && (
                      <span
                        className="font-mono text-[10px] px-2 py-0.5 border border-[#DFFF00]/40 text-[#DFFF00]"
                        title="Multiple visitors flagged the same theme — merged into one canonical correction"
                        data-testid={`corr-cluster-${row.source_message_id}`}
                      >
                        ×{row.cluster_size} visitors
                      </span>
                    )}
                    <span className="ml-auto font-mono text-[10px] text-[#DFFF00]">applied ×{row.applied_count}</span>
                  </div>
                  <div className="mt-1 pl-2 border-l-2 border-[#DFFF00] text-[12px] text-[#D7D7D9] italic">
                    "{row.correction}"
                  </div>
                  {row.similar_members && row.similar_members.length > 0 && (
                    <details className="mt-2 ml-2">
                      <summary className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#8E8E93] cursor-pointer hover:text-white">
                        ↳ {row.similar_members.length} similar visitor{row.similar_members.length === 1 ? "" : "s"} merged into this cluster
                      </summary>
                      <ul className="mt-2 space-y-2 pl-3 border-l border-[#2A2A2A]">
                        {row.similar_members.map((sm, i) => (
                          <li key={i} className="text-[11px]">
                            <div className="text-[#8E8E93]">"{sm.question}"</div>
                            <div className="text-[#D7D7D9] italic">→ "{sm.note}"</div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="ww-card p-6 mb-8">
        <div className="text-overline mb-4 flex items-center gap-2"><ChartBar size={12} weight="fill" /> Monthly trend</div>
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${monthly.length || 1}, minmax(0, 1fr))` }}>
          {monthly.map((m) => {
            const totalH = Math.round((m.total / maxTotal) * 140);
            const uniqueH = Math.round((m.unique / maxTotal) * 140);
            return (
              <div key={m.month} className="flex flex-col items-center gap-2" data-testid={`chart-bar-${m.month}`}>
                <div className="h-[160px] w-full flex items-end justify-center gap-1 px-1">
                  <div className="w-1/2 bg-[#FF4500]/80 hover:bg-[#FF4500] transition-colors" style={{ height: `${Math.max(totalH, 2)}px` }} title={`${m.total} visits`}></div>
                  <div className="w-1/2 bg-[#DFFF00]/70 hover:bg-[#DFFF00] transition-colors" style={{ height: `${Math.max(uniqueH, 2)}px` }} title={`${m.unique} unique`}></div>
                </div>
                <div className="font-mono text-[10px] text-[#8E8E93] uppercase">{fmtMonth(m.month)}</div>
                <div className="font-mono text-[10px] text-white">{m.total} / <span className="text-[#DFFF00]">{m.unique}</span></div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-4 font-mono text-[10px] text-[#8E8E93]">
          <span><span className="inline-block w-3 h-3 bg-[#FF4500] mr-2 align-middle"></span>Total visits</span>
          <span><span className="inline-block w-3 h-3 bg-[#DFFF00] mr-2 align-middle"></span>Unique visitors (by IP)</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="ww-card p-6">
          <div className="text-overline mb-4 flex items-center gap-2"><Globe size={12} weight="fill" /> Top countries</div>
          {(data?.top_countries || []).length === 0 ? (
            <div className="text-[#555] text-sm">No geo data yet. (Cloudflare adds the country header automatically once deployed.)</div>
          ) : (
            <ul className="space-y-2">
              {data.top_countries.map((c) => (
                <li key={c.country} className="flex items-center justify-between text-sm" data-testid={`country-row-${c.country}`}>
                  <span className="font-mono text-xs text-white">{c.country === "??" ? "Unknown / dev" : c.country}</span>
                  <span className="font-mono text-xs text-[#DFFF00]">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="ww-card p-6">
          <div className="text-overline mb-4">◢ Top pages</div>
          {(data?.top_paths || []).length === 0 ? (
            <div className="text-[#555] text-sm">No page-views logged yet.</div>
          ) : (
            <ul className="space-y-2">
              {data.top_paths.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm" data-testid={`path-row-${p.path.replace(/[^a-z0-9]/gi, '_')}`}>
                  <span className="font-mono text-xs text-white truncate max-w-[70%]" title={p.path}>{p.path}</span>
                  <span className="font-mono text-xs text-[#DFFF00]">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="ww-card p-6">
        <div className="text-overline mb-4">◢ Recent visits (last 50)</div>
        {(data?.recent || []).length === 0 ? (
          <div className="text-[#555] text-sm">No recent visits.</div>
        ) : (
          <div className="border border-[#2A2A2A]">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
              <div className="col-span-3">Time</div>
              <div className="col-span-3">Path</div>
              <div className="col-span-2">IP</div>
              <div className="col-span-1">Country</div>
              <div className="col-span-3">Referer</div>
            </div>
            {data.recent.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-2 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] text-xs items-center" data-testid={`recent-row-${idx}`}>
                <div className="col-span-3 font-mono text-[11px] text-[#8E8E93]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
                <div className="col-span-3 text-white font-mono truncate" title={r.path}>{r.path}</div>
                <div className="col-span-2 text-[#DFFF00] font-mono">{r.ip || "—"}</div>
                <div className="col-span-1 font-mono text-[#FF4500]">{r.country || "—"}</div>
                <div className="col-span-3 font-mono text-[10px] text-[#8E8E93] truncate" title={r.referer}>{r.referer || "direct"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SEO helper — quick-links to Google's Rich Results Test for each case study slug */}
      <SEOTools token={token} />

      <div className="mt-6 font-mono text-[10px] text-[#555] leading-relaxed">
        ◢ Tracking is server-side, cookieless. Country is supplied by Cloudflare&apos;s <code className="text-[#DFFF00]">CF-IPCountry</code> header — it appears automatically once requests are proxied through Cloudflare. Known bots are filtered out of these stats.
      </div>
    </div>
  );
}

function SEOTools({ token }) {
  const [studies, setStudies] = useState([]);
  useEffect(() => {
    axios.get(`${API}/case-studies`).then(({ data }) => setStudies(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const site = process.env.REACT_APP_SITE_URL || origin;
  return (
    <div className="ww-card p-6 mb-8" data-testid="analytics-seo-tools">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-overline">◢ SEO · rich results & sitemap</div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        <a href={`${site}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] py-1 px-2" data-testid="seo-view-sitemap">View sitemap.xml →</a>
        <a href={`${site}/api/robots.txt`} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] py-1 px-2" data-testid="seo-view-robots">View robots.txt →</a>
        <a href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(`${site}/kosh`)}`} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] py-1 px-2" data-testid="seo-test-kosh">Test /kosh in Rich Results →</a>
      </div>
      {studies.length === 0 ? (
        <div className="text-[#555] text-sm">No case studies yet.</div>
      ) : (
        <ul className="divide-y divide-[#2A2A2A]">
          {studies.slice(0, 10).map((cs) => {
            const slug = cs.slug || cs.id;
            const csUrl = `${site}/case-studies/${slug}`;
            return (
              <li key={cs.id} className="py-3 flex items-center gap-3 flex-wrap">
                <span className="text-white text-sm flex-1 min-w-[240px]">{cs.title}</span>
                <span className="font-mono text-[10px] text-[#8E8E93]">/case-studies/{slug}</span>
                <a href={csUrl} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] py-1 px-2">Open</a>
                <a href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(csUrl)}`} target="_blank" rel="noreferrer" className="btn-ghost text-[10px] py-1 px-2" data-testid={`seo-test-${slug}`}>Rich Results Test →</a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatTile({ label, value, delta, note, accent, testid }) {
  return (
    <div className="bg-[#050505] p-5" data-testid={testid}>
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8E8E93]">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-display text-3xl" style={{ color: accent }}>{Number(value || 0).toLocaleString()}</div>
        {typeof delta === "number" && (
          <div className={`font-mono text-[11px] ${delta >= 0 ? "text-emerald-400" : "text-[#FF4500]"}`}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%
          </div>
        )}
      </div>
      {note && <div className="mt-1 font-mono text-[9px] text-[#555]">{note}</div>}
    </div>
  );
}
